import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react'
import { useOrganizations } from '@/hooks/use-phenotypes'
import type { Organization } from '@/api/types'

interface OrganizationContextValue {
  selectedOrgId: string
  selectedOrg: Organization | undefined
  organizations: Organization[]
  setSelectedOrgId: (id: string) => void
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null)

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { data: organizations } = useOrganizations()
  const [selectedOrgId, setSelectedOrgId] = useState('')

  // Auto-select first org when orgs load
  useEffect(() => {
    if (organizations?.length && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id)
    }
  }, [organizations, selectedOrgId])

  const selectedOrg = useMemo(
    () => organizations?.find(o => o.id === selectedOrgId),
    [organizations, selectedOrgId],
  )

  const value = useMemo<OrganizationContextValue>(
    () => ({
      selectedOrgId,
      selectedOrg,
      organizations: organizations ?? [],
      setSelectedOrgId,
    }),
    [selectedOrgId, selectedOrg, organizations],
  )

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext)
  if (!ctx) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }
  return ctx
}
